// Attention-queue data layer (NBA shock package, batch 4).
//
// The queue is a pipeline-wide ranking, so it lives on its own key root: a
// failing queue read must not block the deal list or the card, and a deal
// mutation that invalidates `['deals', …]` does not have to refetch a digest
// the user is not looking at.
import { useQuery } from '@tanstack/react-query';

import { useCurrentScope } from '../../../shared/scope/useCurrentScope.js';
import { getDealAttentionQueueQueryKey, getDealsAttentionQueue } from '../api/deals.client.js';

/** Default page size of the dashboard digest; mirrors the API default. */
export const ATTENTION_QUEUE_WIDGET_LIMIT = 5;

/**
 * Open deals of the workspace ranked by money at risk. Scores are computed
 * server-side from the same heuristic as the card, so the widget never
 * re-derives a probability in the browser.
 */
export function useAttentionQueue(limit: number = ATTENTION_QUEUE_WIDGET_LIMIT) {
  const scope = useCurrentScope();
  const workspaceId = scope?.workspaceId ?? 'anonymous';
  return useQuery({
    queryKey: getDealAttentionQueueQueryKey(workspaceId, limit),
    queryFn: () => getDealsAttentionQueue(limit),
    enabled: scope !== null,
    // A dashboard digest is not worth retrying behind the user's back; the
    // widget renders a retry action instead.
    retry: false,
  });
}
